'use client';
import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import {
  ChevronRight, ChevronLeft, Upload, X, Check, Plus, Search,
  Loader2, FileText, AlertCircle, Info, HelpCircle, Save, ExternalLink,
  User, FileBadge, Layers, PackageCheck,
} from 'lucide-react';

interface Props {
  userEmail: string;
  userName: string;
  department: string;
  userId?: string;
  onComplete: () => void;
}

// ─── Constants ────────────────────────────────────────────────────────────

type LangView = 'en' | 'el' | 'both';

const SCHOOLS = [
  'School of Sciences & Engineering',
  'School of Business & Economics',
  'School of Humanities & Social Sciences',
  'School of Architecture',
  'School of Health Sciences',
];
const DEPARTMENTS: Record<string, string[]> = {
  'School of Sciences & Engineering': ['Computer Science & Engineering', 'Electrical Engineering', 'Mathematics'],
  'School of Business & Economics': ['Business Administration', 'Finance & Banking', 'Tourism & Hospitality Management'],
  'School of Humanities & Social Sciences': ['Law', 'Psychology & Education', 'European & French Studies', 'Public Health & Communication'],
  'School of Architecture': ['Architecture & Urban Design'],
  'School of Health Sciences': ['Nursing', 'Pharmacy'],
};

const LANG_ISO_OPTIONS = [
  { iso: 'eng', en: 'English', el: 'Αγγλικά' },
  { iso: 'gre', en: 'Greek', el: 'Ελληνικά' },
  { iso: 'fre', en: 'French', el: 'Γαλλικά' },
  { iso: 'ger', en: 'German', el: 'Γερμανικά' },
  { iso: 'spa', en: 'Spanish', el: 'Ισπανικά' },
  { iso: 'ita', en: 'Italian', el: 'Ιταλικά' },
  { iso: 'tur', en: 'Turkish', el: 'Τουρκικά' },
  { iso: 'rus', en: 'Russian', el: 'Ρωσικά' },
  { iso: 'oth', en: 'Other', el: 'Άλλη' },
];

// Steps are organised into four logical groups for the sidebar. This is the
// "wizard with groups of questions" pattern — identification first (who you
// are), document (what you're submitting), content (titles/abstracts/keywords),
// then finalize (files and review).
interface StepDef { id: number; en: string; el: string; group: string; groupEn: string; groupEl: string; groupIcon: any; }
const STEPS: StepDef[] = [
  { id: 1,  en: 'Student Identification',   el: 'Στοιχεία Φοιτητή',               group: 'ident',    groupEn: 'Identification',  groupEl: 'Ταυτοποίηση',     groupIcon: User },
  { id: 2,  en: 'Document Type & Rights',   el: 'Τύπος Τεκμηρίου & Δικαιώματα',    group: 'document', groupEn: 'Document',        groupEl: 'Τεκμήριο',        groupIcon: FileBadge },
  { id: 3,  en: 'Titles',                   el: 'Τίτλοι',                         group: 'document', groupEn: 'Document',        groupEl: 'Τεκμήριο',        groupIcon: FileBadge },
  { id: 4,  en: 'People & ORCIDs',          el: 'Πρόσωπα & ORCID',                group: 'document', groupEn: 'Document',        groupEl: 'Τεκμήριο',        groupIcon: FileBadge },
  { id: 5,  en: 'Institution & Dates',      el: 'Ίδρυμα & Ημερομηνίες',           group: 'document', groupEn: 'Document',        groupEl: 'Τεκμήριο',        groupIcon: FileBadge },
  { id: 6,  en: 'Abstracts',                el: 'Περιλήψεις',                     group: 'content',  groupEn: 'Content',         groupEl: 'Περιεχόμενο',     groupIcon: Layers },
  { id: 7,  en: 'Subjects & Keywords',      el: 'Θέματα & Λέξεις-κλειδιά',        group: 'content',  groupEn: 'Content',         groupEl: 'Περιεχόμενο',     groupIcon: Layers },
  { id: 8,  en: 'Identifiers & Extras',     el: 'Αναγνωριστικά & Έξτρα',          group: 'content',  groupEn: 'Content',         groupEl: 'Περιεχόμενο',     groupIcon: Layers },
  { id: 9,  en: 'PDF Upload',               el: 'Αρχεία PDF',                     group: 'finalize', groupEn: 'Finalize',        groupEl: 'Ολοκλήρωση',      groupIcon: PackageCheck },
  { id: 10, en: 'Review & Submit',          el: 'Έλεγχος & Υποβολή',              group: 'finalize', groupEn: 'Finalize',        groupEl: 'Ολοκλήρωση',      groupIcon: PackageCheck },
];
const TOTAL_STEPS = STEPS.length;

interface FormData {
  // Step 1 — Student Identification
  studentIdNumber: string;
  studentEmail: string;
  authorSurname: string;
  authorName: string;
  authorPatronymic: string;     // Father's name / patronymic
  passportOrIdNumber: string;   // Passport or national ID
  studentCountry: string;       // Student's country (may differ from institution country)
  thesisLanguage: string;       // Free-text language name

  // Step 2 — Doc type & rights
  docType: 'master' | 'phd' | '';
  dcType: string;
  dcUhtype: string;
  accessRights: 'open' | 'restricted' | 'embargoed' | 'metadata_only' | '';
  license: string;
  embargoMonth: string;
  thesisStatus: 'completed' | 'inprogress';

  // Step 3 — Titles
  titleEn: string;
  titleEl: string;
  titleTertiary: string;

  // Step 4 — People & ORCIDs (some duplicate name fields from step 1,
  // but kept here too so the familiar author block is visible when reviewing people)
  orcid: string;
  supervisorSurname: string;
  supervisorName: string;
  supervisorPatronymic: string;
  committee1: string; committee2: string; committee3: string;
  committeeOrcid1: string; committeeOrcid2: string; committeeOrcid3: string;

  // Step 5 — Institution & dates
  school: string;
  department: string;
  institution: string;
  country: string;               // Institution's country
  languageIso: string;
  dateSubmission: string;
  datePublication: string;
  startDate: string;
  expectedCompletion: string;

  // Step 6 — Abstracts
  abstractEn: string;
  abstractEl: string;
  abstractTertiary: string;

  // Step 7 — Keywords
  uncontrolledKeywords: string;

  // Step 8 — Identifiers & extras
  pid: string;
  extent: string;
  collaboratingInstitution: string;
  fundingSource: string;
}

// ─── Help content registry ────────────────────────────────────────────────
// Every field with a ? icon pulls its modal body from here. ORCID gets a
// bigger block with step-by-step instructions per the user's request.

interface HelpContent { title: string; body: React.ReactNode; }

function helpFor(key: string, view: LangView): HelpContent | null {
  const t = (en: string, el: string) => view === 'el' ? el : view === 'en' ? en : <>{en}<br /><span className="text-[#8892a8]">{el}</span></>;

  switch (key) {
    case 'studentIdNumber':
      return {
        title: view === 'el' ? 'Αριθμός Μητρώου' : 'Student ID Number',
        body: <p>{t(
          'The unique student ID assigned by Philips University (e.g. "201234567"). It appears on your student card and in the student portal. This is the number the librarian uses to look up your record.',
          'Ο μοναδικός αριθμός μητρώου που σας έδωσε το Πανεπιστήμιο Φίλιππος (π.χ. "201234567"). Φαίνεται στην φοιτητική σας ταυτότητα και στην πύλη φοιτητών.'
        )}</p>,
      };

    case 'studentEmail':
      return {
        title: view === 'el' ? 'Email Φοιτητή' : 'Student Email',
        body: <div className="space-y-2">
          <p>{t(
            'The email address where you want to receive decision notifications (approved / sent back / published).',
            'Η διεύθυνση email στην οποία θέλετε να λαμβάνετε ειδοποιήσεις για την διατριβή σας (έγκριση / επιστροφή για διορθώσεις / δημοσίευση).'
          )}</p>
          <p className="text-[#8892a8]">{t(
            'By default we pre-fill this with your signed-in email, but you may change it — for example, if you prefer your personal email to your university one.',
            'Από προεπιλογή είναι το email με το οποίο συνδεθήκατε, αλλά μπορείτε να το αλλάξετε — π.χ. αν προτιμάτε το προσωπικό σας email αντί για το πανεπιστημιακό.'
          )}</p>
        </div>,
      };

    case 'authorName':
      return {
        title: view === 'el' ? 'Όνομα / Επώνυμο' : 'Name / Surname',
        body: <p>{t(
          'Your legal name as it appears on your university record and diploma. Enter them separately in the Surname and Name fields. These will appear on the title page of the archived thesis.',
          'Το νομικό σας όνομα όπως εμφανίζεται στο πτυχίο σας. Συμπληρώστε το Επίθετο και το Όνομα σε ξεχωριστά πεδία. Θα εμφανιστούν στη σελίδα τίτλου της διατριβής.'
        )}</p>,
      };

    case 'authorPatronymic':
      return {
        title: view === 'el' ? 'Πατρώνυμο' : "Father's Name (Patronymic)",
        body: <div className="space-y-2">
          <p>{t(
            "Your father's name. Per the Greek national-standard format, this appears as the initial letter only on the title page (e.g. \"Ιωάννου, Μαρία Α.\" where Α. stands for Ανδρέα).",
            'Το όνομα του πατέρα σας. Σύμφωνα με το εθνικό πρότυπο, εμφανίζεται ως αρχικό γράμμα στη σελίδα τίτλου (π.χ. "Ιωάννου, Μαρία Α." όπου Α. σημαίνει Ανδρέα).'
          )}</p>
          <p className="text-[#8892a8]">{t(
            'You may enter the full name here — it will be rendered as an initial in official outputs.',
            'Μπορείτε να γράψετε εδώ το πλήρες όνομα — θα εμφανιστεί ως αρχικό γράμμα στα επίσημα αρχεία.'
          )}</p>
        </div>,
      };

    case 'passportOrIdNumber':
      return {
        title: view === 'el' ? 'Αριθμός Διαβατηρίου / Ταυτότητας' : 'Passport or National ID Number',
        body: <div className="space-y-2">
          <p>{t(
            "Your Cyprus or national ID number, or your passport number if you're an international student. This is used strictly by the library for identity verification of the deposit and is NOT published on the public repository page.",
            'Ο αριθμός της κυπριακής ή εθνικής σας ταυτότητας, ή ο αριθμός διαβατηρίου αν είστε διεθνής φοιτητής/τρια. Χρησιμοποιείται αποκλειστικά από τη βιβλιοθήκη για την ταυτοποίηση της κατάθεσης και ΔΕΝ δημοσιεύεται στο δημόσιο αποθετήριο.'
          )}</p>
          <p className="text-xs text-blue-700 bg-blue-50 border border-blue-200 rounded-lg p-2">{t(
            'Only staff with librarian or admin role can see this field.',
            'Μόνο το προσωπικό με ρόλο βιβλιοθηκονόμου ή διαχειριστή μπορεί να δει αυτό το πεδίο.'
          )}</p>
        </div>,
      };

    case 'studentCountry':
      return {
        title: view === 'el' ? 'Χώρα Φοιτητή' : 'Student Country',
        body: <p>{t(
          'The country of your citizenship (not necessarily the country of the institution). For example, a Greek citizen studying in Cyprus would enter "Greece". If you hold dual citizenship enter the one on your registration documents.',
          'Η χώρα της υπηκοότητάς σας (δεν είναι απαραίτητα η χώρα του ιδρύματος). Π.χ. ένας Έλληνας φοιτητής που σπουδάζει στην Κύπρο βάζει "Greece". Αν έχετε διπλή υπηκοότητα, βάλτε αυτή των εγγράφων εγγραφής.'
        )}</p>,
      };

    case 'thesisLanguage':
      return {
        title: view === 'el' ? 'Γλώσσα Συγγραφής Διατριβής' : 'Thesis Writing Language',
        body: <p>{t(
          'The main language in which the thesis is written — e.g. English, Greek, French. If your thesis is in multiple languages, select the primary one. The ISO 639/3 code for metadata is set separately in step 5.',
          'Η κύρια γλώσσα στην οποία είναι γραμμένη η διατριβή — π.χ. English, Greek, French. Αν είναι σε πολλές γλώσσες, επιλέξτε την κύρια. Ο κωδικός ISO 639/3 για τα μεταδεδομένα ορίζεται χωριστά στο βήμα 5.'
        )}</p>,
      };

    case 'docType':
      return {
        title: view === 'el' ? 'Τύπος Τεκμηρίου' : 'Document Type',
        body: <p>{t(
          "Master's thesis or Doctoral dissertation. This sets the dc.type metadata field to the EU-compatible vocabulary (info:eu-repo/semantics/masterThesis or doctoralThesis) and is used to categorise your thesis in the repository.",
          'Μεταπτυχιακή ή Διδακτορική διατριβή. Αυτό ορίζει το πεδίο dc.type στο συμβατό λεξιλόγιο της ΕΕ και χρησιμοποιείται για την κατηγοριοποίηση στο αποθετήριο.'
        )}</p>,
      };

    case 'accessRights':
      return {
        title: view === 'el' ? 'Δικαιώματα Πρόσβασης' : 'Access Rights',
        body: <div className="space-y-1.5 text-sm">
          <p><strong>Open Access</strong> — {t('Anyone can read the full PDF.', 'Ο καθένας μπορεί να διαβάσει το πλήρες PDF.')}</p>
          <p><strong>Restricted</strong> — {t('Only authenticated university members can read it.', 'Μόνο συνδεδεμένα μέλη του πανεπιστημίου.')}</p>
          <p><strong>Embargoed</strong> — {t('Becomes open access after a date you specify (common when waiting for a journal publication).', 'Γίνεται ανοιχτή πρόσβαση μετά από ημερομηνία που ορίζετε (συχνά για αναμονή δημοσίευσης σε περιοδικό).')}</p>
          <p><strong>Metadata Only</strong> — {t('Only the title/abstract/keywords are public; no PDF.', 'Μόνο τίτλος/περίληψη/λέξεις δημόσια, χωρίς PDF.')}</p>
        </div>,
      };

    case 'license':
      return {
        title: view === 'el' ? 'Άδεια Creative Commons' : 'Creative Commons License',
        body: <div className="space-y-1.5 text-sm">
          <p><strong>CC BY 4.0</strong> — {t('Others may share and adapt, even commercially, as long as they credit you.', 'Άλλοι μπορούν να μοιραστούν και να προσαρμόσουν με αναφορά.')}</p>
          <p><strong>CC BY-NC 4.0</strong> — {t('As above, but non-commercial use only.', 'Ίδια, αλλά μόνο μη εμπορική χρήση.')}</p>
          <p><strong>CC BY-ND 4.0</strong> — {t('Others may share unchanged, with credit.', 'Άλλοι μπορούν να μοιραστούν αυτούσιο με αναφορά.')}</p>
          <p><strong>CC BY-NC-ND 4.0</strong> — {t('Share unchanged, non-commercial, with credit. Most restrictive CC option.', 'Αυτούσιο, μη εμπορικό, με αναφορά. Η πιο περιοριστική επιλογή CC.')}</p>
          <p><strong>All Rights Reserved</strong> — {t('Traditional copyright — no automatic reuse.', 'Παραδοσιακά πνευματικά δικαιώματα — χωρίς αυτόματη επαναχρησιμοποίηση.')}</p>
          <p className="text-[#8892a8] pt-1">{t('If in doubt, CC BY 4.0 is the most widely accepted choice for open academic work.', 'Αν δεν είστε σίγουροι, το CC BY 4.0 είναι η πιο ευρέως αποδεκτή επιλογή για ακαδημαϊκά έργα ανοιχτής πρόσβασης.')}</p>
        </div>,
      };

    case 'title':
      return {
        title: view === 'el' ? 'Τίτλος Διατριβής' : 'Thesis Title',
        body: <p>{t(
          'The exact title as it appears on the title page of your thesis. Per national standard, titles are stored in up to three languages. Capitalisation and punctuation should match the thesis exactly.',
          'Ο ακριβής τίτλος όπως εμφανίζεται στη σελίδα τίτλου της διατριβής σας. Σύμφωνα με το εθνικό πρότυπο, αποθηκεύονται έως τρεις γλώσσες. Τα κεφαλαία και τα σημεία στίξης πρέπει να ταιριάζουν ακριβώς.'
        )}</p>,
      };

    case 'orcid':
      // ORCID deserves the biggest, richest modal — the user asked for this
      // explicitly. We walk them through account creation end-to-end.
      return {
        title: view === 'el' ? 'ORCID iD — Τι είναι και πώς να το αποκτήσετε' : 'ORCID iD — What it is and how to get one',
        body: (
          <div className="space-y-4 text-sm leading-relaxed">
            <div>
              <p className="font-600 text-[#0f1a35] mb-1">
                {t('What is an ORCID iD?', 'Τι είναι το ORCID iD;')}
              </p>
              <p>{t(
                "ORCID (Open Researcher and Contributor ID) is a free, unique 16-digit identifier for researchers — like a DOI, but for people. It distinguishes you from other researchers with the same name and follows you throughout your career across institutions. It's required for most journal submissions and by many national repositories.",
                'Το ORCID (Open Researcher and Contributor ID) είναι ένα δωρεάν, μοναδικό 16ψήφιο αναγνωριστικό για ερευνητές — σαν DOI, αλλά για ανθρώπους. Σας ξεχωρίζει από άλλους ερευνητές με το ίδιο όνομα και σας ακολουθεί σε όλη την καριέρα σας.'
              )}</p>
              <p className="mt-1 text-[#8892a8] font-mono text-xs">
                {t('Format: 0000-0000-0000-0000', 'Μορφή: 0000-0000-0000-0000')}
              </p>
            </div>

            <div className="border border-[#e2e6f0] rounded-lg p-3 bg-[#f7f8fc]">
              <p className="font-600 text-[#0f1a35] mb-2">
                {t('How to create one (5 minutes)', 'Πώς να το δημιουργήσετε (5 λεπτά)')}
              </p>
              <ol className="list-decimal list-inside space-y-1.5 text-[#4a5578]">
                <li>{t(
                  <>Go to <a href="https://orcid.org/register" target="_blank" rel="noreferrer" className="text-[#1a2e5a] font-600 underline inline-flex items-center gap-1">orcid.org/register<ExternalLink size={10} /></a></>,
                  <>Μεταβείτε στο <a href="https://orcid.org/register" target="_blank" rel="noreferrer" className="text-[#1a2e5a] font-600 underline inline-flex items-center gap-1">orcid.org/register<ExternalLink size={10} /></a></>
                )}</li>
                <li>{t(
                  'Enter your first name, last name, and a primary email (use your university email so your institution is verified automatically).',
                  'Εισάγετε όνομα, επώνυμο και κύριο email (χρησιμοποιήστε το πανεπιστημιακό σας email για αυτόματη επαλήθευση του ιδρύματος).'
                )}</li>
                <li>{t(
                  'Choose a password (at least 8 characters, a number, and a symbol).',
                  'Επιλέξτε κωδικό πρόσβασης (τουλάχιστον 8 χαρακτήρες, ένας αριθμός και ένα σύμβολο).'
                )}</li>
                <li>{t(
                  'Set your default visibility. "Everyone" is the academic norm; you can change it later.',
                  'Ορίστε την προεπιλεγμένη ορατότητα. Το "Everyone" είναι το ακαδημαϊκό πρότυπο· μπορείτε να το αλλάξετε αργότερα.'
                )}</li>
                <li>{t(
                  "Verify your email (click the link ORCID sends) — you can't use the iD until it's verified.",
                  'Επαληθεύστε το email σας (κάντε κλικ στον σύνδεσμο που θα σας στείλει το ORCID) — δεν μπορείτε να χρησιμοποιήσετε το iD μέχρι να επαληθευτεί.'
                )}</li>
                <li>{t(
                  <>Your iD appears at the top of your ORCID profile page in the format <code className="bg-white px-1 rounded">0000-0000-0000-0000</code>. Copy it (including the dashes) and paste it into this field.</>,
                  <>Το iD σας εμφανίζεται στο πάνω μέρος της σελίδας προφίλ ORCID στη μορφή <code className="bg-white px-1 rounded">0000-0000-0000-0000</code>. Αντιγράψτε το (με τις παύλες) και επικολλήστε το εδώ.</>
                )}</li>
              </ol>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
              <p className="font-600 text-amber-800 mb-1">{t('Tips', 'Συμβουλές')}</p>
              <ul className="list-disc list-inside space-y-0.5 text-amber-900">
                <li>{t(
                  "Don't share your password. Third parties should only ever ask you to authorise them via the official ORCID sign-in button.",
                  'Μην μοιράζεστε τον κωδικό σας. Τρίτοι θα ζητούν εξουσιοδότηση μόνο μέσω του επίσημου κουμπιού σύνδεσης ORCID.'
                )}</li>
                <li>{t(
                  "Add your thesis to your ORCID record after it's published — it builds your research profile automatically.",
                  'Προσθέστε τη διατριβή σας στο ORCID μετά τη δημοσίευση — το ερευνητικό σας προφίλ χτίζεται αυτόματα.'
                )}</li>
                <li>{t(
                  'One person = one ORCID. If you already have one from a prior degree, reuse it; do not create a second.',
                  'Ένα άτομο = ένα ORCID. Αν έχετε ήδη από προηγούμενο πτυχίο, χρησιμοποιήστε το ίδιο.'
                )}</li>
              </ul>
            </div>

            <div className="bg-[#1a2e5a]/5 border border-[#1a2e5a]/20 rounded-lg p-3">
              <p className="font-600 text-[#1a2e5a] mb-1">{t('When is it required?', 'Πότε είναι υποχρεωτικό;')}</p>
              <p className="text-[#4a5578]">{t(
                <>An ORCID iD is <strong>required for completed master&apos;s theses</strong>. For <strong>doctoral (PhD) dissertations it is recommended but optional</strong> — you may submit without one. If you enter an iD it must follow the 0000-0000-0000-0000 format.</>,
                <>Το ORCID iD είναι <strong>υποχρεωτικό για ολοκληρωμένες μεταπτυχιακές διατριβές</strong>. Για τις <strong>διδακτορικές διατριβές συνιστάται αλλά είναι προαιρετικό</strong> — μπορείτε να υποβάλετε χωρίς αυτό. Αν το εισάγετε, πρέπει να έχει τη μορφή 0000-0000-0000-0000.</>
              )}</p>
            </div>

            <p className="text-[#8892a8] text-xs">
              {t(
                <>More: <a href="https://info.orcid.org/what-is-orcid/" target="_blank" rel="noreferrer" className="underline">official ORCID documentation</a></>,
                <>Περισσότερα: <a href="https://info.orcid.org/what-is-orcid/" target="_blank" rel="noreferrer" className="underline">επίσημη τεκμηρίωση ORCID</a></>
              )}
            </p>
          </div>
        ),
      };

    case 'committee':
      return {
        title: view === 'el' ? 'Εξεταστική Επιτροπή' : 'Examination Committee',
        body: <p>{t(
          'The members of the committee that examined and approved your thesis. Include up to 3, starting with the chair or internal member. Per national format, enter as "Surname, Name Patronymic-Initial" (e.g. "Παπαδόπουλος, Γεώργιος Δ."). If your thesis is in progress and the committee isn\'t finalised, this step is optional.',
          'Τα μέλη της επιτροπής που εξέτασε και ενέκρινε τη διατριβή σας. Μέχρι 3 μέλη, ξεκινώντας από τον/την πρόεδρο. Σύμφωνα με το εθνικό πρότυπο, σε μορφή "Επίθετο, Όνομα Αρχικό-Πατρωνύμου". Αν η διατριβή είναι υπό εκπόνηση και δεν έχει οριστεί ακόμα η επιτροπή, το βήμα είναι προαιρετικό.'
        )}</p>,
      };

    case 'school':
      return {
        title: view === 'el' ? 'Σχολή & Τμήμα' : 'School & Department',
        body: <p>{t(
          'Select the school (faculty) where your thesis was supervised. Once you pick a school, the Department dropdown is populated with that school\'s departments. If your unit doesn\'t appear, contact the library.',
          'Επιλέξτε τη σχολή στην οποία εκπονήθηκε η διατριβή. Μόλις επιλέξετε σχολή, το πεδίο Τμήμα θα ενημερωθεί με τα τμήματα της. Αν δεν υπάρχει η μονάδα σας, επικοινωνήστε με τη βιβλιοθήκη.'
        )}</p>,
      };

    case 'languageIso':
      return {
        title: view === 'el' ? 'Γλώσσα Τεκμηρίου (ISO 639/3)' : 'Document Language (ISO 639/3)',
        body: <p>{t(
          'The three-letter ISO 639/3 code for the language of the thesis. "eng" = English, "gre" = Greek, "fre" = French. This is what appears in the dc.language metadata field and is what harvesting systems (like OpenAIRE) actually read.',
          'Ο τριψήφιος κωδικός ISO 639/3 για τη γλώσσα της διατριβής. "eng" = Αγγλικά, "gre" = Ελληνικά. Αυτός εμφανίζεται στο πεδίο dc.language και τον διαβάζουν τα συστήματα συγκομιδής (OpenAIRE).'
        )}</p>,
      };

    case 'dateSubmission':
      return {
        title: view === 'el' ? 'Ημερομηνία Υποβολής' : 'Date of Submission',
        body: <p>{t(
          'The date you officially deposit the thesis with the library. Use YYYY-MM-DD. For a completed thesis this is usually the same day you use this form. For an in-progress dissertation it may be a projected date.',
          'Η ημερομηνία που καταθέτετε επισήμως τη διατριβή στη βιβλιοθήκη. Μορφή YYYY-MM-DD. Για ολοκληρωμένη διατριβή είναι συνήθως η σημερινή.'
        )}</p>,
      };

    case 'abstract':
      return {
        title: view === 'el' ? 'Περίληψη' : 'Abstract',
        body: <p>{t(
          'A short summary of your thesis (usually 150–300 words). National standard requires abstracts in both English and Greek. Paste the exact abstract from your thesis document — formatting and paragraphs will be preserved.',
          'Μια σύντομη περίληψη της διατριβής (συνήθως 150–300 λέξεις). Το εθνικό πρότυπο απαιτεί περιλήψεις σε Αγγλικά και Ελληνικά. Επικολλήστε την ακριβή περίληψη από το αρχείο της διατριβής.'
        )}</p>,
      };

    case 'scopus':
      return {
        title: view === 'el' ? 'Θεματικές Κατηγορίες SCOPUS' : 'SCOPUS Subject Categories',
        body: <p>{t(
          'Select up to 10 subject categories from the SCOPUS (ASJC) controlled vocabulary. These are maintained by Elsevier and refreshed hourly by our system. They help readers find your thesis by discipline and are a mandatory field for the national repository.',
          'Επιλέξτε έως 10 θεματικές κατηγορίες από το ελεγχόμενο λεξιλόγιο SCOPUS (ASJC). Συντηρούνται από την Elsevier και ενημερώνονται ωριαία από το σύστημά μας. Βοηθούν στην εύρεση της διατριβής ανά πεδίο και είναι υποχρεωτικό πεδίο για το εθνικό αποθετήριο.'
        )}</p>,
      };

    case 'uncontrolledKeywords':
      return {
        title: view === 'el' ? 'Ελεύθερες Λέξεις-κλειδιά' : 'Uncontrolled Keywords',
        body: <p>{t(
          'Your own keywords in addition to the SCOPUS categories. These let you use specific terminology from your field that may not fit a broad SCOPUS label (e.g. "zero-shot learning", "habermasian public sphere"). One per line or comma-separated.',
          'Δικές σας λέξεις-κλειδιά πέρα από τις κατηγορίες SCOPUS. Επιτρέπουν ειδικούς όρους του πεδίου σας που δεν ταιριάζουν σε ευρείες ετικέτες SCOPUS. Μία ανά γραμμή ή με κόμμα.'
        )}</p>,
      };

    case 'pid':
      return {
        title: view === 'el' ? 'Μόνιμο Αναγνωριστικό (PID / Handle / DOI)' : 'Persistent Identifier (PID / Handle / DOI)',
        body: <p>{t(
          'If your thesis already has a Handle (like http://hdl.handle.net/10442/...) or DOI (like 10.5281/zenodo...), paste it here. If not, leave blank — the library will mint one after approval. This goes into dc.source.uri.',
          'Αν η διατριβή σας έχει ήδη Handle (π.χ. http://hdl.handle.net/10442/...) ή DOI (π.χ. 10.5281/zenodo...), επικολλήστε το εδώ. Αν όχι, αφήστε κενό — θα το εκδώσει η βιβλιοθήκη μετά την έγκριση.'
        )}</p>,
      };

    case 'extent':
      return {
        title: view === 'el' ? 'Έκταση (dc.format.extent)' : 'Format / Extent (dc.format.extent)',
        body: <p>{t(
          'The physical description of the thesis. Example: "xviii, 320 σ.; 30 εκ." means "18 front-matter pages in Roman numerals, 320 main pages, height 30 cm". Follow the format of the printed/bound copy.',
          'Η φυσική περιγραφή της διατριβής. Π.χ. "xviii, 320 σ.; 30 εκ." σημαίνει "18 εισαγωγικές σελίδες σε λατινικούς αριθμούς, 320 κύριες σελίδες, ύψος 30 εκ."'
        )}</p>,
      };

    case 'collaboratingInstitution':
      return {
        title: view === 'el' ? 'Συνεργαζόμενο Ίδρυμα' : 'Collaborating Institution(s)',
        body: <p>{t(
          'If your thesis was produced in partnership with another institution (joint degree, research exchange, co-supervision), name them here. Multiple institutions separated by semicolons.',
          'Αν η διατριβή εκπονήθηκε σε συνεργασία με άλλο ίδρυμα (κοινός τίτλος σπουδών, ανταλλαγή, συνεπίβλεψη), καταγράψτε τα εδώ, χωρισμένα με ελληνικό ερωτηματικό.'
        )}</p>,
      };

    case 'fundingSource':
      return {
        title: view === 'el' ? 'Πηγή Χρηματοδότησης' : 'Funding Source',
        body: <p>{t(
          'Grant numbers, project IDs, or funding body names that supported this research. Example: "EU H2020 no. 101017014" or "Cyprus Research Foundation EXCELLENCE/1216/0204".',
          'Αριθμοί χορηγιών, κωδικοί έργων ή ονόματα φορέων που στήριξαν την έρευνα. Π.χ. "EU H2020 no. 101017014" ή "Ίδρυμα Έρευνας Κύπρου EXCELLENCE/1216/0204".'
        )}</p>,
      };

    case 'pdfUpload':
      return {
        title: view === 'el' ? 'Αρχείο PDF Διατριβής' : 'Thesis PDF Upload',
        body: <div className="space-y-2">
          <p>{t(
            'The full text of the thesis as a PDF. Max 50MB per file. If your thesis exists in both English and Greek, upload both; otherwise upload the single language version.',
            'Το πλήρες κείμενο της διατριβής σε PDF. Μέγιστο 50MB ανά αρχείο. Αν υπάρχει σε Αγγλικά και Ελληνικά, ανεβάστε και τα δύο· αλλιώς την μονογλωσσική έκδοση.'
          )}</p>
          <p className="text-[#8892a8]">{t(
            'Make sure the PDF is searchable (OCR-applied for scanned documents) and not password-protected — archives need to index the text.',
            'Βεβαιωθείτε ότι το PDF είναι αναζητήσιμο (με OCR αν είναι σαρωμένο) και δεν έχει κωδικό — τα αρχεία χρειάζεται να ευρετηριάσουν το κείμενο.'
          )}</p>
        </div>,
      };

    default:
      return null;
  }
}

// ─── Component ─────────────────────────────────────────────────────────────

export default function SubmissionForm({ userEmail, userName, department, userId, onComplete }: Props) {
  const [view, setView] = useState<LangView>('both');
  const [currentStep, setCurrentStep] = useState(1);
  const [pdfEn, setPdfEn] = useState<File | null>(null);
  const [pdfEl, setPdfEl] = useState<File | null>(null);
  const [uploadLanguage, setUploadLanguage] = useState<'en' | 'el' | 'both'>('both');
  const [includeThirdLang, setIncludeThirdLang] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSavingDraft, setIsSavingDraft] = useState(false);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [scopusKeywords, setScopusKeywords] = useState<{ code: string; label: string; area: string }[]>([]);
  const [selectedScopus, setSelectedScopus] = useState<{ code: string; label: string; area: string }[]>([]);
  const [loadingKeywords, setLoadingKeywords] = useState(true);
  const [kwSearch, setKwSearch] = useState('');
  const [kwAreaFilter, setKwAreaFilter] = useState('All');
  const [helpKey, setHelpKey] = useState<string | null>(null); // Currently-open help modal

  const { register, watch, formState: { errors }, setValue, trigger, getValues, reset } = useForm<FormData>({
    defaultValues: {
      institution: 'Philips University',
      country: 'Cyprus',
      languageIso: 'eng',
      thesisStatus: 'completed',
      studentEmail: userEmail || '',
      // Seed name/surname from user profile if available (space-split)
      authorName: (userName || '').split(' ')[0] || '',
      authorSurname: (userName || '').split(' ').slice(1).join(' ') || '',
      studentCountry: 'Cyprus',
      thesisLanguage: 'English',
    },
  });

  // Load draft + SCOPUS keywords on mount. Draft takes priority because the
  // student may have saved progress in a previous session.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { keywordService, draftService } = await import('@/lib/services/thesisService');
        const data = await keywordService.getAll();
        if (!cancelled) setScopusKeywords(data.map((k: any) => ({ code: k.code, label: k.label, area: k.area })));

        if (userId) {
          const draft = await draftService.load(userId);
          if (!cancelled && draft) {
            reset(draft.payload.form || {});
            setCurrentStep(Math.max(1, Math.min(TOTAL_STEPS, draft.currentStep || 1)));
            setView(draft.viewLang || 'both');
            setSelectedScopus(draft.payload.selectedScopus || []);
            setIncludeThirdLang(Boolean(draft.payload.includeThirdLang));
            setUploadLanguage(draft.payload.uploadLanguage || 'both');
            toast.info(`Resumed draft from ${draft.source === 'db' ? 'your account' : 'this browser'}.`, { duration: 4000 });
          }
        }
      } catch (err) {
        console.log('Error loading draft/keywords:', err);
      } finally {
        if (!cancelled) {
          setLoadingKeywords(false);
          setDraftLoaded(true);
        }
      }
    })();
    return () => { cancelled = true; };
  }, [userId, reset]);

  const watchDocType = watch('docType');
  const watchAccessRights = watch('accessRights');
  const watchSchool = watch('school');
  const watchThesisStatus = watch('thesisStatus');
  const availableDepts = watchSchool ? (DEPARTMENTS[watchSchool] || []) : [];

  // ORCID policy: required only for completed Master's theses.
  // PhD candidates may leave it blank (ORCID iD is recommended but not mandated
  // for doctoral deposits at this institution); in-progress theses of any type
  // are also allowed to skip it.
  const orcidRequired = watchThesisStatus === 'completed' && watchDocType === 'master';

  useEffect(() => {
    if (watchDocType === 'phd') {
      setValue('dcType', 'info:eu-repo/semantics/doctoralThesis');
      setValue('dcUhtype', 'Διδακτορική Διατριβή / Doctoral Thesis');
    } else if (watchDocType === 'master') {
      setValue('dcType', 'info:eu-repo/semantics/masterThesis');
      setValue('dcUhtype', "Μεταπτυχιακή Διατριβή / Master's Thesis");
    }
  }, [watchDocType, setValue]);

  const filteredKeywords = scopusKeywords.filter((kw) => {
    const matchSearch = !kwSearch || kw.label.toLowerCase().includes(kwSearch.toLowerCase()) || kw.code.toLowerCase().includes(kwSearch.toLowerCase());
    const matchArea = kwAreaFilter === 'All' || kw.area === kwAreaFilter;
    return matchSearch && matchArea;
  });
  const kwAreas = ['All', ...Array.from(new Set(scopusKeywords.map((k) => k.area)))];

  const toggleScopus = (kw: { code: string; label: string; area: string }) => {
    setSelectedScopus((prev) =>
      prev.find((k) => k.code === kw.code)
        ? prev.filter((k) => k.code !== kw.code)
        : prev.length < 10 ? [...prev, kw] : prev,
    );
  };

  // Save draft — wired to the Save Progress button AND to an autosave effect
  // that fires when the step changes (so you never lose progress if you forget).
  const saveDraft = useCallback(async (opts: { silent?: boolean } = {}) => {
    if (!userId || !draftLoaded) return;
    setIsSavingDraft(true);
    try {
      const { draftService } = await import('@/lib/services/thesisService');
      const payload = {
        form: getValues(),
        selectedScopus,
        includeThirdLang,
        uploadLanguage,
      };
      const result = await draftService.save(userId, payload, currentStep, view);
      setLastSavedAt(new Date().toISOString());
      if (!opts.silent) {
        if (result.db) toast.success('Draft saved to your account.');
        else if (result.local) toast.success('Draft saved locally in this browser.');
        else toast.error(result.error || 'Could not save draft.');
      }
    } catch (err: any) {
      if (!opts.silent) toast.error(err?.message || 'Draft save failed.');
    } finally {
      setIsSavingDraft(false);
    }
  }, [userId, draftLoaded, getValues, selectedScopus, includeThirdLang, uploadLanguage, currentStep, view]);

  // Autosave 400ms after step changes (doesn't fire on initial mount).
  const mountedRef = useRef(false);
  useEffect(() => {
    if (!mountedRef.current) { mountedRef.current = true; return; }
    const t = setTimeout(() => saveDraft({ silent: true }), 400);
    return () => clearTimeout(t);
  }, [currentStep, saveDraft]);

  const handleNext = async () => {
    let valid = true;
    if (currentStep === 1) {
      valid = await trigger(['studentIdNumber', 'studentEmail', 'authorSurname', 'authorName', 'studentCountry', 'thesisLanguage']);
    } else if (currentStep === 2) {
      valid = await trigger(['docType', 'accessRights', 'license']);
    } else if (currentStep === 3) {
      const v = getValues();
      const anyTitle = v.titleEn?.trim() || v.titleEl?.trim();
      if (!anyTitle) { toast.error('At least one title is required.'); return; }
    } else if (currentStep === 4) {
      valid = await trigger(['supervisorSurname', 'supervisorName']);
      // Committee is required for any completed thesis; ORCID has its own stricter rule.
      if (valid && watchThesisStatus === 'completed') valid = await trigger(['committee1']);
      if (valid && orcidRequired) valid = await trigger(['orcid']);
      // Even when ORCID isn't required, if the student *did* type something, validate the format.
      if (valid && !orcidRequired) valid = await trigger(['orcid']);
    } else if (currentStep === 5) {
      valid = await trigger(['school', 'department', 'dateSubmission']);
    } else if (currentStep === 6) {
      const v = getValues();
      const anyAbstract = v.abstractEn?.trim() || v.abstractEl?.trim();
      if (!anyAbstract) { toast.error('At least one abstract is required.'); return; }
    } else if (currentStep === 7) {
      if (selectedScopus.length === 0) { toast.error('Select at least one SCOPUS subject category.'); return; }
    } else if (currentStep === 9) {
      if (uploadLanguage === 'en' && !pdfEn) { toast.error('Please upload the English PDF.'); return; }
      if (uploadLanguage === 'el' && !pdfEl) { toast.error('Please upload the Greek PDF.'); return; }
      if (uploadLanguage === 'both' && (!pdfEn || !pdfEl)) { toast.error('Please upload both PDFs.'); return; }
      if (!pdfEn && !pdfEl) { toast.error('Please upload at least one PDF.'); return; }
    }
    if (valid) setCurrentStep((s) => Math.min(s + 1, TOTAL_STEPS));
  };

  const handlePrev = () => setCurrentStep((s) => Math.max(s - 1, 1));

  const jumpToStep = (n: number) => {
    // Only allow jumping to a step you've already reached (+1 for the "next" step).
    // This prevents accidentally skipping validation.
    if (n <= currentStep + 1) setCurrentStep(n);
  };

  const onSubmit = async () => {
    setIsSubmitting(true);
    try {
      const { thesisService, draftService } = await import('@/lib/services/thesisService');
      const f = getValues();

      let primaryLang: 'en' | 'el';
      let titlePrimary: string, titleSecondary: string;
      let abstractPrimary: string, abstractSecondary: string;
      let langCode: string;

      if (view === 'el') {
        primaryLang = 'el';
        titlePrimary = f.titleEl || f.titleEn || '';
        titleSecondary = '';
        abstractPrimary = f.abstractEl || f.abstractEn || '';
        abstractSecondary = '';
        langCode = 'Greek';
      } else if (view === 'en') {
        primaryLang = 'en';
        titlePrimary = f.titleEn || f.titleEl || '';
        titleSecondary = '';
        abstractPrimary = f.abstractEn || f.abstractEl || '';
        abstractSecondary = '';
        langCode = 'English';
      } else {
        primaryLang = 'en';
        titlePrimary = f.titleEn || '';
        titleSecondary = f.titleEl || '';
        abstractPrimary = f.abstractEn || '';
        abstractSecondary = f.abstractEl || '';
        langCode = 'English';
      }

      const scopusCategoriesStr = selectedScopus.map((k) => k.label).join(', ');
      const uncontrolled = (f.uncontrolledKeywords || '').split(/[\n,]/).map((s) => s.trim()).filter(Boolean);

      const keywordsForInsert = [
        ...selectedScopus.map((k) => ({ code: k.code, label: k.label, area: k.area })),
        ...uncontrolled.map((label) => ({
          code: `UNC-${label.toUpperCase().replace(/\s+/g, '-').slice(0, 40)}`,
          label,
          area: 'Uncontrolled',
        })),
      ];

      const payload = {
        docType: f.docType,
        dcType: f.dcType,
        dcUhtype: f.dcUhtype,
        accessRights: f.accessRights,
        license: f.license,
        embargoDate: f.embargoMonth || '',
        thesisStatus: f.thesisStatus,
        studentIdNumber: f.studentIdNumber,
        studentEmail: f.studentEmail,
        passportOrIdNumber: f.passportOrIdNumber,
        studentCountry: f.studentCountry,
        thesisLanguage: f.thesisLanguage,
        titlePrimary, titleSecondary, titleTertiary: f.titleTertiary || '',
        abstractPrimary, abstractSecondary, abstractTertiary: f.abstractTertiary || '',
        authorSurname: f.authorSurname, authorName: f.authorName, authorPatronymic: f.authorPatronymic,
        orcid: f.orcid,
        supervisorSurname: f.supervisorSurname, supervisorName: f.supervisorName, supervisorPatronymic: f.supervisorPatronymic,
        committee1: f.committee1, committee2: f.committee2, committee3: f.committee3,
        committeeOrcid1: f.committeeOrcid1, committeeOrcid2: f.committeeOrcid2, committeeOrcid3: f.committeeOrcid3,
        school: f.school, department: f.department, institution: f.institution, country: f.country,
        languageIso: f.languageIso,
        langCode,
        dateSubmission: f.dateSubmission, datePublication: f.datePublication,
        startDate: f.startDate, expectedCompletion: f.expectedCompletion,
        pid: f.pid, extent: f.extent, collaboratingInstitution: f.collaboratingInstitution, fundingSource: f.fundingSource,
        scopusCategories: scopusCategoriesStr,
        formVariant: 'default',
      };

      const result = await thesisService.submit(payload, keywordsForInsert, pdfEn, pdfEl, userId || '');
      if (result) {
        if (userId) await draftService.clear(userId);
        toast.success('Thesis submitted successfully! Your submission is now under review.');
        onComplete();
      } else {
        toast.error('Submission failed. Please try again.');
      }
    } catch (err: any) {
      toast.error(err?.message || 'Submission failed. Please try again.');
    } finally {
      setIsSubmitting(false);
    }
  };

  const showEn = view === 'en' || view === 'both';
  const showEl = view === 'el' || view === 'both';
  const step = STEPS[currentStep - 1];

  // Group steps for the sidebar. We walk the STEPS array building
  // { group, label, steps[] } chunks so each group can be rendered as a
  // collapsible header with the member steps underneath.
  const groupedSteps = useMemo(() => {
    const out: { group: string; label: string; icon: any; steps: StepDef[] }[] = [];
    for (const s of STEPS) {
      const last = out[out.length - 1];
      const label = view === 'el' ? s.groupEl : s.groupEn;
      if (last && last.group === s.group) {
        last.steps.push(s);
      } else {
        out.push({ group: s.group, label, icon: s.groupIcon, steps: [s] });
      }
    }
    return out;
  }, [view]);

  return (
    <div className="max-w-7xl mx-auto">
      {/* ── TOP TOOLBAR: flag toggle + save button + autosave indicator ── */}
      <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
        <FlagToggle view={view} onChange={setView} />

        <div className="flex items-center gap-2">
          {lastSavedAt && (
            <span className="text-xs text-[#8892a8] hidden sm:block">
              {view === 'el' ? 'Τελευταία αποθήκευση' : 'Last saved'}: {new Date(lastSavedAt).toLocaleTimeString()}
            </span>
          )}
          <button type="button" onClick={() => saveDraft()} disabled={isSavingDraft || !draftLoaded}
            className="flex items-center gap-1.5 text-xs font-600 text-[#1a2e5a] bg-white border border-[#1a2e5a]/20 hover:bg-[#1a2e5a]/5 px-3 py-1.5 rounded-lg transition-all disabled:opacity-50">
            {isSavingDraft ? <Loader2 size={12} className="animate-spin" /> : <Save size={12} />}
            <BT en="Save Progress" el="Αποθήκευση Προόδου" view={view} />
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[260px_1fr] gap-6">
        {/* ── LEFT SIDEBAR: grouped step navigation ────────────────── */}
        <aside className="lg:sticky lg:top-20 lg:self-start">
          <nav className="bg-white border border-[#e2e6f0] rounded-2xl p-3 shadow-sm">
            {groupedSteps.map((g, gi) => {
              const Icon = g.icon;
              const isActiveGroup = g.steps.some((s) => s.id === currentStep);
              return (
                <div key={g.group} className={gi > 0 ? 'mt-2 pt-2 border-t border-[#f0f2f8]' : ''}>
                  <div className={`flex items-center gap-2 px-2 py-1.5 text-[11px] font-700 uppercase tracking-wider ${isActiveGroup ? 'text-[#1a2e5a]' : 'text-[#8892a8]'}`}>
                    <Icon size={12} />
                    <span>{g.label}</span>
                  </div>
                  <div className="space-y-0.5 mt-0.5">
                    {g.steps.map((s) => {
                      const done = s.id < currentStep;
                      const active = s.id === currentStep;
                      const reachable = s.id <= currentStep + 1;
                      return (
                        <button
                          key={s.id}
                          type="button"
                          onClick={() => jumpToStep(s.id)}
                          disabled={!reachable}
                          className={`w-full flex items-center gap-2 px-2.5 py-2 rounded-lg text-xs text-left transition-all ${
                            active ? 'bg-[#1a2e5a] text-white font-600' :
                            done ? 'text-[#4a5578] hover:bg-[#f7f8fc]' :
                            reachable ? 'text-[#4a5578] hover:bg-[#f7f8fc]' :
                            'text-[#c0c8d8] cursor-not-allowed'
                          }`}>
                          <span className={`w-5 h-5 rounded-full flex items-center justify-center text-[10px] font-700 flex-shrink-0 ${
                            active ? 'bg-white text-[#1a2e5a]' :
                            done ? 'bg-emerald-100 text-emerald-700' :
                            'bg-[#f0f2f8] text-[#8892a8]'
                          }`}>
                            {done ? <Check size={10} /> : s.id}
                          </span>
                          <span className="truncate">{view === 'el' ? s.el : s.en}</span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </nav>
        </aside>

        {/* ── MAIN FORM PANEL ──────────────────────────────────────── */}
        <div className="bg-white border border-[#e2e6f0] rounded-2xl overflow-hidden shadow-sm">
          <div className="bg-[#1a2e5a]/4 border-b border-[#e2e6f0] px-6 py-4">
            <div className="flex items-center gap-2">
              <span className="w-6 h-6 rounded-full bg-[#1a2e5a] text-white text-xs font-700 flex items-center justify-center">{currentStep}</span>
              <h2 className="text-sm font-700 text-[#0f1a35]">
                <BT en={step.en} el={step.el} view={view} />
              </h2>
              <span className="text-xs text-[#8892a8] ml-auto">
                {view === 'el' ? 'Βήμα' : 'Step'} {currentStep} / {TOTAL_STEPS}
              </span>
            </div>
          </div>

          <div className="p-6">
            {/* ─── STEP 1: Student Identification ─────────────────────── */}
            {currentStep === 1 && (
              <div className="space-y-5 animate-fade-in">
                <InfoBanner
                  en="Tell us who you are. These fields travel with your thesis record — they identify you to the library and are how we contact you about your submission."
                  el="Πείτε μας ποιος/α είστε. Αυτά τα πεδία ταξιδεύουν με το αρχείο της διατριβής σας — σας ταυτοποιούν στη βιβλιοθήκη και έτσι επικοινωνούμε μαζί σας για την υποβολή."
                  view={view}
                />

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="Student ID Number" el="Αριθμός Μητρώου" view={view} required helpKey="studentIdNumber" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('studentIdNumber', { required: 'Student ID is required' })} className="input-field" />
                    {errors.studentIdNumber && <p className="text-red-500 text-xs mt-1">{errors.studentIdNumber.message}</p>}
                  </div>
                  <div>
                    <BL en="Student Email (for notifications)" el="Email Φοιτητή (για ειδοποιήσεις)" view={view} required helpKey="studentEmail" onOpenHelp={setHelpKey} />
                    <input type="email" {...register('studentEmail', {
                      required: 'Email is required',
                      pattern: { value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/, message: 'Invalid email' },
                    })} className="input-field" />
                    {errors.studentEmail && <p className="text-red-500 text-xs mt-1">{errors.studentEmail.message}</p>}
                  </div>
                </div>

                <div>
                  <BL en="Name & Surname" el="Όνομα & Επώνυμο" view={view} required helpKey="authorName" onOpenHelp={setHelpKey} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <input placeholder={view === 'el' ? 'Όνομα' : 'Name'}
                      {...register('authorName', { required: 'Name is required' })} className="input-field" />
                    <input placeholder={view === 'el' ? 'Επώνυμο' : 'Surname'}
                      {...register('authorSurname', { required: 'Surname is required' })} className="input-field" />
                  </div>
                  {(errors.authorName || errors.authorSurname) && (
                    <p className="text-red-500 text-xs mt-1">{errors.authorName?.message || errors.authorSurname?.message}</p>
                  )}
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="Father's Name (Patronymic)" el="Πατρώνυμο" view={view} helpKey="authorPatronymic" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('authorPatronymic')} className="input-field"
                      placeholder={view === 'el' ? 'π.χ. Ανδρέας' : 'e.g. Andreas'} />
                  </div>
                  <div>
                    <BL en="Passport / National ID Number" el="Αριθμός Διαβατηρίου / Ταυτότητας" view={view} helpKey="passportOrIdNumber" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('passportOrIdNumber')} className="input-field font-mono" />
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="Country (Citizenship)" el="Χώρα (Υπηκοότητα)" view={view} required helpKey="studentCountry" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('studentCountry', { required: 'Country is required' })} className="input-field" />
                    {errors.studentCountry && <p className="text-red-500 text-xs mt-1">{errors.studentCountry.message}</p>}
                  </div>
                  <div>
                    <BL en="Thesis Language" el="Γλώσσα Συγγραφής Διατριβής" view={view} required helpKey="thesisLanguage" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('thesisLanguage', { required: 'Language is required' })} className="input-field"
                      placeholder={view === 'el' ? 'π.χ. Αγγλικά' : 'e.g. English'} />
                    {errors.thesisLanguage && <p className="text-red-500 text-xs mt-1">{errors.thesisLanguage.message}</p>}
                  </div>
                </div>
              </div>
            )}

            {/* ─── STEP 2: Document Type & Rights ─────────────────────── */}
            {currentStep === 2 && (
              <div className="space-y-5 animate-fade-in">
                <div>
                  <BL en="Thesis Status" el="Κατάσταση Διατριβής" view={view} required />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { value: 'completed', en: 'Completed Thesis', el: 'Ολοκληρωμένη Διατριβή' },
                      { value: 'inprogress', en: 'Dissertation in Progress', el: 'Υπό Εκπόνηση' },
                    ].map((opt) => (
                      <label key={opt.value} className={`flex items-start gap-3 border-2 rounded-xl p-4 cursor-pointer transition-all ${watchThesisStatus === opt.value ? 'border-[#1a2e5a] bg-[#1a2e5a]/4' : 'border-[#e2e6f0] hover:border-[#1a2e5a]/40'}`}>
                        <input type="radio" value={opt.value} {...register('thesisStatus')} className="mt-0.5 accent-[#1a2e5a]" />
                        <div>
                          <div className="text-sm font-600 text-[#0f1a35]"><BT en={opt.en} el={opt.el} view={view} /></div>
                        </div>
                      </label>
                    ))}
                  </div>
                </div>

                <div>
                  <BL en="Type of Document (dc.type)" el="Τύπος Εγγράφου (dc.type)" view={view} required helpKey="docType" onOpenHelp={setHelpKey} />
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                    {[
                      { value: 'master', en: "Master's Thesis", el: 'Μεταπτυχιακή Διατριβή', code: 'info:eu-repo/semantics/masterThesis' },
                      { value: 'phd', en: 'Doctoral Dissertation', el: 'Διδακτορική Διατριβή', code: 'info:eu-repo/semantics/doctoralThesis' },
                    ].map((opt) => (
                      <label key={opt.value} className={`flex items-start gap-3 border-2 rounded-xl p-4 cursor-pointer transition-all ${watchDocType === opt.value ? 'border-[#1a2e5a] bg-[#1a2e5a]/4' : 'border-[#e2e6f0] hover:border-[#1a2e5a]/40'}`}>
                        <input type="radio" value={opt.value} {...register('docType', { required: 'Document type is required' })} className="mt-0.5 accent-[#1a2e5a]" />
                        <div>
                          <div className="text-sm font-600 text-[#0f1a35]"><BT en={opt.en} el={opt.el} view={view} /></div>
                          <div className="text-xs text-[#8892a8] mt-0.5 font-mono">{opt.code}</div>
                        </div>
                      </label>
                    ))}
                  </div>
                  {errors.docType && <p className="text-red-500 text-xs mt-1">{errors.docType.message}</p>}
                </div>

                <div>
                  <BL en="Access Rights" el="Δικαιώματα Πρόσβασης" view={view} required helpKey="accessRights" onOpenHelp={setHelpKey} />
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    {[
                      { value: 'open', en: 'Open Access', el: 'Ανοιχτή Πρόσβαση' },
                      { value: 'restricted', en: 'Restricted', el: 'Περιορισμένη' },
                      { value: 'embargoed', en: 'Embargoed', el: 'Αναστολή' },
                      { value: 'metadata_only', en: 'Metadata Only', el: 'Μόνο Μεταδεδομένα' },
                    ].map((opt) => (
                      <label key={opt.value} className={`flex flex-col items-center gap-2 border-2 rounded-xl p-3 cursor-pointer transition-all text-center ${watchAccessRights === opt.value ? 'border-[#1a2e5a] bg-[#1a2e5a]/4' : 'border-[#e2e6f0] hover:border-[#1a2e5a]/40'}`}>
                        <input type="radio" value={opt.value} {...register('accessRights', { required: 'Access rights are required' })} className="accent-[#1a2e5a]" />
                        <span className="text-xs font-600 text-[#0f1a35]"><BT en={opt.en} el={opt.el} view={view} /></span>
                      </label>
                    ))}
                  </div>
                  {errors.accessRights && <p className="text-red-500 text-xs mt-1">{errors.accessRights.message}</p>}
                </div>

                {watchAccessRights === 'embargoed' && (
                  <div className="animate-fade-in">
                    <BL en="Embargo Release (Year-Month)" el="Ημερομηνία Άρσης (Έτος-Μήνας)" view={view} />
                    <input type="month" {...register('embargoMonth')} className="input-field max-w-xs" />
                  </div>
                )}

                <div>
                  <BL en="Creative Commons License (dc.rights.title)" el="Άδεια Creative Commons (dc.rights.title)" view={view} required helpKey="license" onOpenHelp={setHelpKey} />
                  <select {...register('license', { required: 'License is required' })} className="input-field max-w-md">
                    <option value="">{view === 'el' ? '-- Επιλέξτε Άδεια --' : '-- Select License --'}</option>
                    <option value="CC BY 4.0">CC BY 4.0</option>
                    <option value="CC BY-NC 4.0">CC BY-NC 4.0</option>
                    <option value="CC BY-ND 4.0">CC BY-ND 4.0</option>
                    <option value="CC BY-NC-ND 4.0">CC BY-NC-ND 4.0</option>
                    <option value="CC BY-SA 4.0">CC BY-SA 4.0</option>
                    <option value="All Rights Reserved">All Rights Reserved</option>
                  </select>
                  {errors.license && <p className="text-red-500 text-xs mt-1">{errors.license.message}</p>}
                </div>
              </div>
            )}

            {/* ─── STEP 3: Titles ─────────────────────────────────────── */}
            {currentStep === 3 && (
              <div className="space-y-4 animate-fade-in">
                <InfoBanner
                  en="Enter the thesis title in every language available. The national standard stores up to three."
                  el="Εισάγετε τον τίτλο της διατριβής σε κάθε διαθέσιμη γλώσσα. Το εθνικό πρότυπο αποθηκεύει έως τρεις."
                  view={view}
                />

                {showEn && (
                  <div>
                    <BL en="Title (in English)" el="Τίτλος στα Αγγλικά" view={view} required={view !== 'el'} helpKey="title" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('titleEn')} className="input-field" />
                  </div>
                )}
                {showEl && (
                  <div>
                    <BL en="Title (in Greek)" el="Τίτλος στα Ελληνικά" view={view} required={view !== 'en'} helpKey="title" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('titleEl')} className="input-field" />
                  </div>
                )}

                {!includeThirdLang ? (
                  <button type="button" onClick={() => setIncludeThirdLang(true)}
                    className="inline-flex items-center gap-2 text-xs font-600 text-[#1a2e5a] hover:underline">
                    <Plus size={12} />
                    <BT en="Add third-language title (optional)" el="Προσθήκη τίτλου σε τρίτη γλώσσα (προαιρετικό)" view={view} />
                  </button>
                ) : (
                  <div className="animate-fade-in">
                    <div className="flex items-center justify-between">
                      <BL en="Title (third language)" el="Τίτλος (τρίτη γλώσσα)" view={view} />
                      <button type="button" onClick={() => setIncludeThirdLang(false)} className="text-xs text-[#8892a8] hover:text-red-500">
                        <BT en="remove" el="αφαίρεση" view={view} />
                      </button>
                    </div>
                    <input type="text" {...register('titleTertiary')} className="input-field" />
                  </div>
                )}
              </div>
            )}

            {/* ─── STEP 4: People & ORCIDs ──────────────────────────────── */}
            {currentStep === 4 && (
              <div className="space-y-5 animate-fade-in">
                <section>
                  <h3 className="text-xs font-700 text-[#0f1a35] uppercase tracking-wider mb-2 flex items-center gap-2">
                    <BT en="Your ORCID iD" el="ORCID iD σας" view={view} />
                    <HelpTrigger helpKey="orcid" onOpenHelp={setHelpKey} prominent />
                    {orcidRequired && <span className="text-red-500">*</span>}
                    {!orcidRequired && (
                      <span className="text-[10px] font-500 text-[#8892a8] normal-case tracking-normal">
                        ({view === 'el' ? 'προαιρετικό' : 'optional'})
                      </span>
                    )}
                  </h3>
                  <input type="text" placeholder="0000-0000-0000-0000"
                    {...register('orcid', {
                      required: orcidRequired ? 'ORCID is required for completed master\'s theses' : false,
                      pattern: {
                        // Still enforce the format *if* they provide a value, even when optional.
                        value: /^$|^\d{4}-\d{4}-\d{4}-\d{4}$/,
                        message: 'Format: 0000-0000-0000-0000',
                      },
                    })}
                    className="input-field max-w-xs font-mono" />
                  <p className="text-xs text-[#8892a8] mt-1">
                    {watchDocType === 'phd' ? (
                      <BT
                        en="Doctoral candidates may leave this blank — an ORCID iD is recommended but not required for PhD submissions."
                        el="Οι υποψήφιοι διδάκτορες μπορούν να το αφήσουν κενό — το ORCID iD συνιστάται αλλά δεν είναι υποχρεωτικό για διδακτορικές διατριβές."
                        view={view}
                      />
                    ) : (
                      <BT
                        en={<>Don&apos;t have one yet? Click the <HelpCircle size={11} className="inline" /> icon above for step-by-step instructions.</>}
                        el={<>Δεν έχετε ακόμα; Κάντε κλικ στο εικονίδιο <HelpCircle size={11} className="inline" /> για οδηγίες βήμα-βήμα.</>}
                        view={view}
                      />
                    )}
                  </p>
                  {errors.orcid && <p className="text-red-500 text-xs mt-1">{errors.orcid.message}</p>}
                </section>

                <section>
                  <h3 className="text-xs font-700 text-[#0f1a35] uppercase tracking-wider mb-2">
                    <BT en="Supervisor" el="Επιβλέπων/ουσα" view={view} /> <span className="text-red-500">*</span>
                  </h3>
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                    <input placeholder={view === 'el' ? 'Επίθετο' : 'Surname'}
                      {...register('supervisorSurname', { required: 'Supervisor surname is required' })} className="input-field" />
                    <input placeholder={view === 'el' ? 'Όνομα' : 'Name'}
                      {...register('supervisorName', { required: 'Supervisor name is required' })} className="input-field" />
                    <input placeholder={view === 'el' ? 'Αρχικό Πατρωνύμου' : 'Patronymic Initial'}
                      {...register('supervisorPatronymic')} className="input-field" />
                  </div>
                </section>

                <section>
                  <h3 className="text-xs font-700 text-[#0f1a35] uppercase tracking-wider mb-2 flex items-center gap-2">
                    <BT en="Examination Committee (up to 3 members)" el="Εξεταστική Επιτροπή (έως 3 μέλη)" view={view} />
                    <HelpTrigger helpKey="committee" onOpenHelp={setHelpKey} />
                    {watchThesisStatus === 'completed' && <span className="text-red-500">*</span>}
                  </h3>
                  <div className="space-y-2">
                    {([1, 2, 3] as const).map((n) => (
                      <div key={`cm-${n}`} className="grid grid-cols-1 md:grid-cols-2 gap-2">
                        <input
                          placeholder={view === 'el' ? `Μέλος ${n} — Επίθετο, Όνομα Α.Π.` : `Committee Member ${n} — Surname, Name Initial`}
                          {...register(`committee${n}` as keyof FormData, {
                            required: watchThesisStatus === 'completed' && n === 1 ? 'At least one committee member is required' : false,
                          })}
                          className="input-field" />
                        <input placeholder="ORCID (optional) 0000-0000-0000-0000"
                          {...register(`committeeOrcid${n}` as keyof FormData)}
                          className="input-field font-mono" />
                      </div>
                    ))}
                  </div>
                  {errors.committee1 && <p className="text-red-500 text-xs mt-1">{errors.committee1.message}</p>}
                </section>
              </div>
            )}

            {/* ─── STEP 5: Institution & Dates ──────────────────────────── */}
            {currentStep === 5 && (
              <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="School / Faculty" el="Σχολή" view={view} required helpKey="school" onOpenHelp={setHelpKey} />
                    <select {...register('school', { required: 'School is required' })} className="input-field"
                      onChange={(e) => { setValue('school', e.target.value); setValue('department', ''); }}>
                      <option value="">-- {view === 'el' ? 'Επιλέξτε Σχολή' : 'Select School'} --</option>
                      {SCHOOLS.map((s) => <option key={s} value={s}>{s}</option>)}
                    </select>
                    {errors.school && <p className="text-red-500 text-xs mt-1">{errors.school.message}</p>}
                  </div>
                  <div>
                    <BL en="Department" el="Τμήμα" view={view} required helpKey="school" onOpenHelp={setHelpKey} />
                    <select {...register('department', { required: 'Department is required' })} className="input-field">
                      <option value="">-- {view === 'el' ? 'Επιλέξτε Τμήμα' : 'Select Department'} --</option>
                      {availableDepts.map((d) => <option key={d} value={d}>{d}</option>)}
                    </select>
                    {errors.department && <p className="text-red-500 text-xs mt-1">{errors.department.message}</p>}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <BL en="Institution" el="Ίδρυμα" view={view} required />
                    <input {...register('institution')} className="input-field bg-gray-50" readOnly />
                  </div>
                  <div>
                    <BL en="Country of Institution" el="Χώρα Ιδρύματος" view={view} required />
                    <input {...register('country', { required: 'Country is required' })} className="input-field" />
                  </div>
                  <div>
                    <BL en="Document Language (ISO 639/3)" el="Γλώσσα Τεκμηρίου (ISO 639/3)" view={view} required helpKey="languageIso" onOpenHelp={setHelpKey} />
                    <select {...register('languageIso')} className="input-field">
                      {LANG_ISO_OPTIONS.map((o) => (
                        <option key={o.iso} value={o.iso}>{o.iso} — {view === 'el' ? o.el : o.en}</option>
                      ))}
                    </select>
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="Date of Deposit / Submission" el="Ημερομηνία Κατάθεσης" view={view} required helpKey="dateSubmission" onOpenHelp={setHelpKey} />
                    <input type="date" {...register('dateSubmission', { required: 'Date is required' })} className="input-field" />
                    {errors.dateSubmission && <p className="text-red-500 text-xs mt-1">{errors.dateSubmission.message}</p>}
                  </div>
                  <div>
                    <BL en="Date of Publication" el="Ημερομηνία Δημοσίευσης" view={view} />
                    <input type="date" {...register('datePublication')} className="input-field" />
                  </div>
                </div>

                {watchThesisStatus === 'inprogress' && (
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in">
                    <div>
                      <BL en="Start Date of Dissertation" el="Ημερομηνία Έναρξης ΔΔ" view={view} />
                      <input type="month" {...register('startDate')} className="input-field" />
                    </div>
                    <div>
                      <BL en="Expected Completion Date" el="Αναμενόμενη Ημ. Ολοκλήρωσης" view={view} />
                      <input type="month" {...register('expectedCompletion')} className="input-field" />
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* ─── STEP 6: Abstracts ─────────────────────────────────────── */}
            {currentStep === 6 && (
              <div className="space-y-4 animate-fade-in">
                <InfoBanner
                  en="The national-standard spec requires abstracts in both English and Greek when the thesis is bilingual. Add a third-language abstract only if your thesis uses a third language."
                  el="Το εθνικό πρότυπο απαιτεί περιλήψεις σε Αγγλικά και Ελληνικά όταν η διατριβή είναι δίγλωσση."
                  view={view}
                />
                {showEn && (
                  <div>
                    <BL en="Abstract (English)" el="Περίληψη (Αγγλικά)" view={view} required={view !== 'el'} helpKey="abstract" onOpenHelp={setHelpKey} />
                    <textarea rows={5} {...register('abstractEn')} className="input-field resize-none" />
                  </div>
                )}
                {showEl && (
                  <div>
                    <BL en="Abstract (Greek)" el="Περίληψη (Ελληνικά)" view={view} required={view !== 'en'} helpKey="abstract" onOpenHelp={setHelpKey} />
                    <textarea rows={5} {...register('abstractEl')} className="input-field resize-none" />
                  </div>
                )}
                {includeThirdLang && (
                  <div className="animate-fade-in">
                    <BL en="Abstract (third language)" el="Περίληψη (τρίτη γλώσσα)" view={view} helpKey="abstract" onOpenHelp={setHelpKey} />
                    <textarea rows={4} {...register('abstractTertiary')} className="input-field resize-none" />
                  </div>
                )}
              </div>
            )}

            {/* ─── STEP 7: Subjects & Keywords ──────────────────────────── */}
            {currentStep === 7 && (
              <div className="space-y-4 animate-fade-in">
                <InfoBanner
                  en="Select SCOPUS categories from the controlled list (mandatory), then add your own uncontrolled keywords in free text."
                  el="Επιλέξτε θεματικές κατηγορίες SCOPUS (υποχρεωτικό) και προσθέστε δικές σας λέξεις-κλειδιά."
                  view={view}
                />
                <div className="flex items-center justify-between">
                  <h3 className="text-sm font-700 text-[#0f1a35] flex items-center gap-2">
                    <BT en="SCOPUS Subject Categories" el="Θεματικές κατηγορίες SCOPUS" view={view} />
                    <HelpTrigger helpKey="scopus" onOpenHelp={setHelpKey} />
                    <span className="text-red-500">*</span>
                  </h3>
                  <span className="text-xs text-[#8892a8]">{selectedScopus.length}/10 {view === 'el' ? 'επιλεγμένες' : 'selected'}</span>
                </div>
                {selectedScopus.length > 0 && (
                  <div className="flex flex-wrap gap-2 p-3 bg-[#1a2e5a]/4 border border-[#1a2e5a]/15 rounded-xl">
                    {selectedScopus.map((kw) => (
                      <span key={`s-${kw.code}`} className="inline-flex items-center gap-1.5 bg-[#1a2e5a] text-white px-2.5 py-1 rounded-lg text-xs font-600">
                        {kw.label}
                        <button type="button" onClick={() => toggleScopus(kw)} className="hover:text-red-300"><X size={10} /></button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex flex-col sm:flex-row gap-2">
                  <div className="relative flex-1">
                    <Search size={13} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#8892a8]" />
                    <input type="text" value={kwSearch} onChange={(e) => setKwSearch(e.target.value)}
                      placeholder={view === 'el' ? 'Αναζήτηση SCOPUS...' : 'Search SCOPUS...'}
                      className="input-field pl-8 text-xs" />
                  </div>
                  <select value={kwAreaFilter} onChange={(e) => setKwAreaFilter(e.target.value)}
                    className="input-field text-xs sm:max-w-[220px]">
                    {kwAreas.map((a) => <option key={a} value={a}>{a}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto pr-1">
                  {filteredKeywords.map((kw) => {
                    const isSelected = selectedScopus.some((k) => k.code === kw.code);
                    const isDisabled = !isSelected && selectedScopus.length >= 10;
                    return (
                      <button key={`sopt-${kw.code}`} type="button" disabled={isDisabled} onClick={() => toggleScopus(kw)}
                        className={`flex items-center gap-2 border rounded-lg px-3 py-2 text-xs text-left transition-all ${
                          isSelected ? 'border-[#1a2e5a] bg-[#1a2e5a]/8 text-[#1a2e5a] font-600'
                          : isDisabled ? 'border-[#e2e6f0] text-[#8892a8] cursor-not-allowed opacity-50'
                          : 'border-[#e2e6f0] text-[#4a5578] hover:border-[#1a2e5a] hover:text-[#1a2e5a] cursor-pointer'
                        }`}>
                        <div className={`w-3.5 h-3.5 rounded border flex-shrink-0 flex items-center justify-center ${isSelected ? 'bg-[#1a2e5a] border-[#1a2e5a]' : 'border-[#c0c8d8]'}`}>
                          {isSelected && <Check size={9} className="text-white" />}
                        </div>
                        <span className="truncate">{kw.label}</span>
                      </button>
                    );
                  })}
                </div>
                {loadingKeywords && <div className="text-center py-6 text-xs text-[#8892a8]">Loading...</div>}
                {!loadingKeywords && scopusKeywords.length === 0 && (
                  <div className="text-center py-6 text-xs text-[#8892a8]">
                    <BT en="No SCOPUS keywords available. Ask an admin to sync from Elsevier."
                        el="Δεν υπάρχουν διαθέσιμες λέξεις SCOPUS. Ζητήστε από έναν διαχειριστή συγχρονισμό." view={view} />
                  </div>
                )}
                <div className="pt-3 border-t border-[#e2e6f0]">
                  <BL en="Uncontrolled Keywords (author-provided)" el="Μη-ελεγχόμενες λέξεις-κλειδιά (από τον/την συγγραφέα)"
                      view={view} helpKey="uncontrolledKeywords" onOpenHelp={setHelpKey} />
                  <p className="text-xs text-[#8892a8] mb-2">
                    <BT en="One per line or comma-separated." el="Μία ανά γραμμή ή με κόμμα." view={view} />
                  </p>
                  <textarea rows={3} {...register('uncontrolledKeywords')} className="input-field resize-none font-mono" />
                </div>
              </div>
            )}

            {/* ─── STEP 8: Identifiers & Extras ─────────────────────────── */}
            {currentStep === 8 && (
              <div className="space-y-4 animate-fade-in">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="PID / Handle / DOI (dc.source.uri)" el="PID / Handle / DOI (dc.source.uri)" view={view} helpKey="pid" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('pid')} className="input-field font-mono" placeholder="http://hdl.handle.net/..." />
                  </div>
                  <div>
                    <BL en="Format / Extent (dc.format.extent)" el="Έκταση (dc.format.extent)" view={view} helpKey="extent" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('extent')} className="input-field" placeholder="xviii, 320 σ.; 30 εκ." />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="Collaborating Institution(s)" el="Συνεργαζόμενο/α Ίδρυμα/τα" view={view} helpKey="collaboratingInstitution" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('collaboratingInstitution')} className="input-field" />
                  </div>
                  <div>
                    <BL en="Funding Source" el="Χρηματοδότηση" view={view} helpKey="fundingSource" onOpenHelp={setHelpKey} />
                    <input type="text" {...register('fundingSource')} className="input-field" placeholder="Project code or grant ID" />
                  </div>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <BL en="dc.type (auto)" el="dc.type (αυτόματο)" view={view} />
                    <input type="text" {...register('dcType')} className="input-field bg-gray-50 font-mono" readOnly />
                  </div>
                  <div>
                    <BL en="dc.uhtype (auto)" el="dc.uhtype (αυτόματο)" view={view} />
                    <input type="text" {...register('dcUhtype')} className="input-field bg-gray-50" readOnly />
                  </div>
                </div>
              </div>
            )}

            {/* ─── STEP 9: PDF Upload ────────────────────────────────────── */}
            {currentStep === 9 && (
              <div className="space-y-4 animate-fade-in">
                <InfoBanner
                  en="Upload the thesis PDF(s). At least one is required."
                  el="Ανεβάστε τα αρχεία PDF. Απαιτείται τουλάχιστον ένα."
                  view={view}
                />
                <div>
                  <BL en="Upload Language(s)" el="Γλώσσα Αρχείων" view={view} helpKey="pdfUpload" onOpenHelp={setHelpKey} />
                  <div className="grid grid-cols-3 gap-2">
                    {(['en', 'el', 'both'] as const).map((u) => (
                      <button key={u} type="button" onClick={() => setUploadLanguage(u)}
                        className={`text-xs border-2 rounded-lg py-2 transition-all font-600 ${uploadLanguage === u ? 'border-[#1a2e5a] bg-[#1a2e5a]/5 text-[#1a2e5a]' : 'border-[#e2e6f0] text-[#4a5578] hover:border-[#1a2e5a]/40'}`}>
                        {u === 'en' ? 'English only' : u === 'el' ? 'Greek only' : 'Both EN + EL'}
                      </button>
                    ))}
                  </div>
                </div>
                <PdfUploader label="English PDF" file={pdfEn} setFile={setPdfEn}
                  required={uploadLanguage === 'en' || uploadLanguage === 'both'} />
                <PdfUploader label="Greek PDF" file={pdfEl} setFile={setPdfEl}
                  required={uploadLanguage === 'el' || uploadLanguage === 'both'} />
              </div>
            )}

            {/* ─── STEP 10: Review & Submit ──────────────────────────────── */}
            {currentStep === 10 && (
              <div className="space-y-5 animate-fade-in">
                <ReviewGrid values={getValues()} selectedScopus={selectedScopus} pdfEn={pdfEn} pdfEl={pdfEl} view={view} />

                <div className="bg-amber-50 border border-amber-200 rounded-xl p-4">
                  <div className="flex items-start gap-2">
                    <AlertCircle size={14} className="text-amber-600 flex-shrink-0 mt-0.5" />
                    <div className="text-xs text-amber-800 space-y-1">
                      <p className="font-700"><BT en="Before you submit:" el="Πριν υποβάλετε:" view={view} /></p>
                      <ul className="space-y-0.5 ml-2">
                        <li>• <BT en="This is a one-time submission. The librarian may send it back if revisions are needed."
                              el="Η υποβολή είναι μοναδική. Ο/Η βιβλιοθηκονόμος μπορεί να την επιστρέψει για διορθώσεις." view={view} /></li>
                        <li>• <BT en="Keywords may be adjusted during approval."
                              el="Οι λέξεις-κλειδιά μπορεί να προσαρμοστούν κατά την έγκριση." view={view} /></li>
                        <li>• <BT en="You will be notified at the student email when a decision is made."
                              el="Θα ειδοποιηθείτε στο email σας όταν ληφθεί απόφαση." view={view} /></li>
                      </ul>
                    </div>
                  </div>
                </div>

                <div className="flex items-center gap-2">
                  <input type="checkbox" id="confirm-submit" className="accent-[#1a2e5a] w-4 h-4" required />
                  <label htmlFor="confirm-submit" className="text-xs text-[#4a5578]">
                    <BT en="I confirm all information is accurate and I understand this is a one-time submission."
                        el="Επιβεβαιώνω ότι όλες οι πληροφορίες είναι ακριβείς και κατανοώ ότι πρόκειται για μοναδική υποβολή." view={view} />
                  </label>
                </div>
              </div>
            )}
          </div>

          <div className="border-t border-[#e2e6f0] px-6 py-4 flex items-center justify-between bg-gray-50/50">
            <button type="button" onClick={handlePrev} disabled={currentStep === 1}
              className="flex items-center gap-2 px-4 py-2 text-xs font-600 text-[#4a5578] bg-white border border-[#e2e6f0] hover:border-[#1a2e5a] rounded-lg transition-all disabled:opacity-40 disabled:cursor-not-allowed">
              <ChevronLeft size={14} /> <BT en="Previous" el="Προηγούμενο" view={view} />
            </button>
            {currentStep < TOTAL_STEPS ? (
              <button type="button" onClick={handleNext}
                className="flex items-center gap-2 px-5 py-2 text-xs font-600 text-white bg-[#1a2e5a] hover:bg-[#111e3d] rounded-lg transition-all">
                <BT en="Next" el="Επόμενο" view={view} /> <ChevronRight size={14} />
              </button>
            ) : (
              <button type="button" onClick={onSubmit} disabled={isSubmitting}
                className="flex items-center gap-2 px-6 py-2.5 text-sm font-600 text-white bg-emerald-600 hover:bg-emerald-700 rounded-lg transition-all min-w-[140px] justify-center disabled:opacity-60">
                {isSubmitting ? (
                  <><Loader2 size={14} className="animate-spin" /> <BT en="Submitting..." el="Υποβολή..." view={view} /></>
                ) : (
                  <><Check size={14} /> <BT en="Submit Thesis" el="Υποβολή Διατριβής" view={view} /></>
                )}
              </button>
            )}
          </div>
        </div>
      </div>

      {/* ── Help modal ── */}
      {helpKey && (
        <HelpModal helpKey={helpKey} view={view} onClose={() => setHelpKey(null)} />
      )}
    </div>
  );
}

// ─── Subcomponents ────────────────────────────────────────────────────────

// Bilingual Label WITH an optional ? icon that opens the help modal.
function BL({ en, el, view, required, className, helpKey, onOpenHelp }: {
  en: string; el: string; view: LangView; required?: boolean; className?: string;
  helpKey?: string; onOpenHelp?: (key: string) => void;
}) {
  const labelText = view === 'en' ? <>{en}</>
    : view === 'el' ? <>{el}</>
    : <><span>{en}</span><span className="text-[#8892a8] normal-case font-500"> / </span><span className="text-[#8892a8]">{el}</span></>;
  return (
    <label className={`text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1 flex items-center gap-1.5 ${className || ''}`}>
      <span>{labelText}</span>
      {required && <span className="text-red-500">*</span>}
      {helpKey && onOpenHelp && <HelpTrigger helpKey={helpKey} onOpenHelp={onOpenHelp} />}
    </label>
  );
}

function BT({ en, el, view }: { en: React.ReactNode; el: React.ReactNode; view: LangView }) {
  if (view === 'en') return <>{en}</>;
  if (view === 'el') return <>{el}</>;
  return <>{en} / <span className="text-[#8892a8]">{el}</span></>;
}

// The "?" icon that opens the help modal. `prominent` renders it larger and
// in brand colour — used for ORCID where the user asked for extra emphasis.
function HelpTrigger({ helpKey, onOpenHelp, prominent }: {
  helpKey: string; onOpenHelp: (key: string) => void; prominent?: boolean;
}) {
  if (prominent) {
    return (
      <button
        type="button"
        onClick={(e) => { e.preventDefault(); onOpenHelp(helpKey); }}
        className="inline-flex items-center gap-1 text-[#1a2e5a] hover:text-[#c9a84c] bg-[#c9a84c]/10 hover:bg-[#c9a84c]/20 rounded-full px-2 py-0.5 text-[10px] font-600 transition-all"
        aria-label="Help"
      >
        <HelpCircle size={11} />
        How to create one?
      </button>
    );
  }
  return (
    <button
      type="button"
      onClick={(e) => { e.preventDefault(); onOpenHelp(helpKey); }}
      className="text-[#8892a8] hover:text-[#1a2e5a] transition-colors"
      aria-label="Help"
    >
      <HelpCircle size={13} />
    </button>
  );
}

function HelpModal({ helpKey, view, onClose }: { helpKey: string; view: LangView; onClose: () => void }) {
  const content = helpFor(helpKey, view);
  // Close on ESC
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);
  if (!content) return null;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/40 animate-fade-in" onClick={onClose}>
      <div
        className="bg-white rounded-2xl shadow-2xl border border-[#e2e6f0] w-full max-w-lg max-h-[85vh] overflow-hidden flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between gap-3 px-5 py-4 border-b border-[#e2e6f0] bg-[#f7f8fc]">
          <div className="flex items-center gap-2">
            <HelpCircle size={16} className="text-[#1a2e5a]" />
            <h3 className="text-sm font-700 text-[#0f1a35]">{content.title}</h3>
          </div>
          <button type="button" onClick={onClose} className="text-[#8892a8] hover:text-red-500 transition-colors" aria-label="Close">
            <X size={18} />
          </button>
        </div>
        <div className="px-5 py-4 overflow-y-auto text-sm text-[#4a5578] leading-relaxed">
          {content.body}
        </div>
        <div className="px-5 py-3 border-t border-[#e2e6f0] bg-gray-50/60 flex justify-end">
          <button type="button" onClick={onClose} className="text-xs font-600 text-white bg-[#1a2e5a] hover:bg-[#111e3d] px-4 py-1.5 rounded-lg transition-all">
            {view === 'el' ? 'Κλείσιμο' : 'Got it'}
          </button>
        </div>
      </div>
    </div>
  );
}

function FlagToggle({ view, onChange }: { view: LangView; onChange: (v: LangView) => void }) {
  const options: { value: LangView; flag: React.ReactNode; label: string }[] = [
    { value: 'en', flag: <UKFlag />, label: 'EN' },
    { value: 'el', flag: <GRFlag />, label: 'ΕΛ' },
    { value: 'both', flag: <BothFlags />, label: 'Both' },
  ];
  return (
    <div className="flex items-center gap-2">
      <span className="text-xs text-[#8892a8] font-600 uppercase tracking-wide mr-1 hidden sm:block">
        Show fields in:
      </span>
      <div className="inline-flex items-center gap-1 bg-white border border-[#e2e6f0] rounded-xl p-1 shadow-sm">
        {options.map((o) => {
          const active = view === o.value;
          return (
            <button key={o.value} onClick={() => onChange(o.value)}
              className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-600 transition-all ${
                active ? 'bg-[#1a2e5a] text-white shadow-sm' : 'text-[#4a5578] hover:bg-[#f7f8fc]'
              }`}>
              <span className="flex items-center">{o.flag}</span>
              <span>{o.label}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function UKFlag() {
  return (
    <svg width="16" height="12" viewBox="0 0 60 30" xmlns="http://www.w3.org/2000/svg" className="rounded-sm border border-white/40">
      <clipPath id="uk-clip"><path d="M0,0 v30 h60 v-30 z" /></clipPath>
      <g clipPath="url(#uk-clip)">
        <path d="M0,0 v30 h60 v-30 z" fill="#012169" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#fff" strokeWidth="6" />
        <path d="M0,0 L60,30 M60,0 L0,30" stroke="#C8102E" strokeWidth="4" clipPath="url(#uk-clip)" />
        <path d="M30,0 v30 M0,15 h60" stroke="#fff" strokeWidth="10" />
        <path d="M30,0 v30 M0,15 h60" stroke="#C8102E" strokeWidth="6" />
      </g>
    </svg>
  );
}
function GRFlag() {
  return (
    <svg width="16" height="12" viewBox="0 0 27 18" xmlns="http://www.w3.org/2000/svg" className="rounded-sm border border-white/40">
      <rect width="27" height="18" fill="#0D5EAF" />
      <rect y="2" width="27" height="2" fill="#fff" />
      <rect y="6" width="27" height="2" fill="#fff" />
      <rect y="10" width="27" height="2" fill="#fff" />
      <rect y="14" width="27" height="2" fill="#fff" />
      <rect y="16" width="27" height="2" fill="#fff" />
      <rect width="10" height="10" fill="#0D5EAF" />
      <rect x="4" width="2" height="10" fill="#fff" />
      <rect y="4" width="10" height="2" fill="#fff" />
    </svg>
  );
}
function BothFlags() {
  return <span className="inline-flex items-center gap-0.5"><UKFlag /><GRFlag /></span>;
}

function InfoBanner({ en, el, view }: { en: string; el: string; view: LangView }) {
  return (
    <div className="flex items-start gap-2 bg-blue-50 border border-blue-200 rounded-xl p-3">
      <Info size={14} className="text-blue-600 flex-shrink-0 mt-0.5" />
      <p className="text-xs text-blue-800 leading-relaxed">
        {view === 'en' && en}
        {view === 'el' && el}
        {view === 'both' && <>{en}<br /><span className="text-blue-700/80">{el}</span></>}
      </p>
    </div>
  );
}

function PdfUploader({ label, file, setFile, required }:
  { label: string; file: File | null; setFile: (f: File | null) => void; required: boolean }) {
  return (
    <div>
      <label className="text-xs font-600 text-[#4a5578] uppercase tracking-wide mb-1.5 block">
        {label} {required ? <span className="text-red-500">*</span> : <span className="text-[#8892a8] normal-case font-400">(optional)</span>}
      </label>
      {file ? (
        <div className="flex items-center gap-3 border border-emerald-200 bg-emerald-50 rounded-xl p-4">
          <FileText size={18} className="text-emerald-600" />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-600 text-emerald-800 truncate">{file.name}</p>
            <p className="text-xs text-emerald-600">{(file.size / 1024 / 1024).toFixed(2)} MB</p>
          </div>
          <button type="button" onClick={() => setFile(null)} className="text-emerald-600 hover:text-red-500"><X size={16} /></button>
        </div>
      ) : (
        <label className="flex flex-col items-center justify-center border-2 border-dashed border-[#e2e6f0] hover:border-[#1a2e5a] rounded-xl p-8 cursor-pointer transition-all group">
          <Upload size={24} className="text-[#8892a8] group-hover:text-[#1a2e5a] mb-3 transition-colors" />
          <p className="text-sm font-600 text-[#4a5578]">Click to upload {label}</p>
          <p className="text-xs text-[#8892a8] mt-1">PDF only, max 50MB</p>
          <input type="file" accept=".pdf" className="hidden"
            onChange={(e) => {
              const f = e.target.files?.[0];
              if (f) {
                if (f.size > 50 * 1024 * 1024) { toast.error('File too large. Maximum 50MB.'); return; }
                if (f.type !== 'application/pdf') { toast.error('Only PDF files are accepted.'); return; }
                setFile(f);
              }
            }} />
        </label>
      )}
    </div>
  );
}

function ReviewGrid({ values, selectedScopus, pdfEn, pdfEl, view }:
  { values: FormData; selectedScopus: any[]; pdfEn: File | null; pdfEl: File | null; view: LangView }) {
  const rows: [string, string, string][] = [
    ['Student ID', 'Αριθμός Μητρώου', values.studentIdNumber || '—'],
    ['Student Email', 'Email Φοιτητή', values.studentEmail || '—'],
    ['Name', 'Όνομα', values.authorName || '—'],
    ['Surname', 'Επώνυμο', values.authorSurname || '—'],
    ["Father's Name", 'Πατρώνυμο', values.authorPatronymic || '—'],
    ['Passport/ID Number', 'Αριθμός Διαβατηρίου/Ταυτότητας', values.passportOrIdNumber || '—'],
    ['Country', 'Χώρα', values.studentCountry || '—'],
    ['Thesis Language', 'Γλώσσα Συγγραφής', values.thesisLanguage || '—'],
    ['Document Type', 'Τύπος Εγγράφου', values.docType === 'phd' ? 'Διδακτορική' : "Μεταπτυχιακή"],
    ['Access Rights', 'Δικαιώματα Πρόσβασης', values.accessRights || '—'],
    ['License', 'Άδεια', values.license || '—'],
    ['Author ORCID', 'ORCID', values.orcid || '—'],
    ['Supervisor', 'Επιβλέπων/ουσα', [values.supervisorSurname, values.supervisorName, values.supervisorPatronymic].filter(Boolean).join(', ')],
    ['Committee', 'Επιτροπή', [values.committee1, values.committee2, values.committee3].filter(Boolean).join(' | ') || '—'],
    ['School', 'Σχολή', values.school || '—'],
    ['Department', 'Τμήμα', values.department || '—'],
    ['Language ISO', 'Γλώσσα ISO', values.languageIso || '—'],
    ['Date of Submission', 'Ημ. Κατάθεσης', values.dateSubmission || '—'],
    ['Title (English)', 'Τίτλος (Αγγλικά)', values.titleEn || '—'],
    ['Title (Greek)', 'Τίτλος (Ελληνικά)', values.titleEl || '—'],
    ['SCOPUS Categories', 'Θεματικές SCOPUS', selectedScopus.map((k) => k.label).join(', ') || '—'],
    ['Uncontrolled Keywords', 'Ελεύθερες Λέξεις', values.uncontrolledKeywords || '—'],
    ['PID', 'PID', values.pid || '—'],
    ['Extent', 'Έκταση', values.extent || '—'],
    ['PDF (English)', 'PDF (Αγγλικά)', pdfEn ? pdfEn.name : '—'],
    ['PDF (Greek)', 'PDF (Ελληνικά)', pdfEl ? pdfEl.name : '—'],
  ];
  return (
    <div className="bg-[#1a2e5a]/4 border border-[#1a2e5a]/15 rounded-xl p-4">
      <h3 className="text-sm font-700 text-[#1a2e5a] mb-3">
        <BT en="Submission Summary" el="Σύνοψη Υποβολής" view={view} />
      </h3>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2 text-xs">
        {rows.map(([en, el, v]) => (
          <div key={en} className="flex flex-col gap-0.5 min-w-0">
            <span className="text-[#8892a8] font-600 uppercase tracking-wide text-[10px]">
              {view === 'en' ? en : view === 'el' ? el : `${en} / ${el}`}
            </span>
            <span className="text-[#0f1a35] font-500 truncate" title={v}>{v}</span>
          </div>
        ))}
      </div>
    </div>
  );
}
